import {
  draggable,
  dropTargetForElements,
} from '@atlaskit/pragmatic-drag-and-drop/adapter/element-adapter';
import { combine } from '@atlaskit/pragmatic-drag-and-drop/utils/combine';
import { attachClosestEdge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge/attach-closest-edge';
import { extractClosestEdge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge/extract-closest-edge';
import type { Edge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/types';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { type DraggedResource, groupCardData, groupOf, resourceOf } from '@/entities/section';
import {
  type GroupChild,
  type GroupChildKind,
  groupPreview,
  hiddenLabel,
  type WorkspaceGroup,
} from '@/entities/workspace-group';
import { ResourceLabelChips } from '@/features/resource-labels';
import { Icons, MenuItem, MenuList, MenuRow, Popover } from '@/shared/ui';

import styles from './GroupCard.module.css';

const KIND_ICON: Record<GroupChildKind, () => React.JSX.Element> = {
  board: Icons.board,
  database: Icons.layers,
  document: Icons.document,
  group: Icons.group,
};

/** A miniature of one child: its type and a hint of its name, never its contents. */
function Mini({ child }: { child: GroupChild }) {
  const { t } = useTranslation();
  const Icon = KIND_ICON[child.kind];
  const kind = t(child.kind === 'group' ? 'groups.title' : `workspace.objectTypes.${child.kind}`);
  return (
    <span className={styles.mini} data-kind={child.kind} title={`${kind}: ${child.name}`}>
      <Icon />
      <span className={styles.miniName}>{child.name}</span>
    </span>
  );
}

export interface GroupCardActions {
  onOpen: () => void;
  /** Put the address of the group on the clipboard. */
  onCopyLink?: (() => void) | undefined;
  /** Edit the labels of the group itself. */
  onLabels?: (() => void) | undefined;
  onRename?: (() => void) | undefined;
  onMove?: (() => void) | undefined;
  /** The group is nested or filed: put it one level up. */
  onMoveOut?: (() => void) | undefined;
  onDissolve?: (() => void) | undefined;
  /** A resource was dropped on the card: file it inside. */
  onDropResource?: ((source: DraggedResource) => void) | undefined;
  /** A group was dropped: inside this one, or beside it. */
  onDropGroup?:
    ((id: string, anchor?: { after_id: string } | { before_id: string }) => void) | undefined;
}

/**
 * A group as a catalog card: a layered tile holding a 2x2 miniature of its first children (what
 * this viewer may see) and a counter of the rest. The miniatures are decoration: the whole card
 * opens the group and is the one drag handle, so a click or a drag never has two meanings.
 */
export function GroupCard({
  group,
  items,
  location,
  actions,
}: {
  group: WorkspaceGroup;
  items: readonly GroupChild[];
  location?: string | undefined;
  actions: GroupCardActions;
}) {
  const { t } = useTranslation();
  const ref = useRef<HTMLLIElement>(null);
  const [dragging, setDragging] = useState(false);
  const [zone, setZone] = useState<Edge | 'into' | null>(null);
  const { shown, hidden } = groupPreview(items);
  const { onDropResource, onDropGroup } = actions;
  const manageable = onDropGroup !== undefined;

  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    const data = groupCardData(group.workspace_id, group.id);
    const accepts = (source: Record<string | symbol, unknown>) => {
      const resource = resourceOf(source);
      if (resource) return resource.workspaceId === group.workspace_id && !!onDropResource;
      const moving = groupOf(source);
      return (
        moving !== null &&
        moving.workspaceId === group.workspace_id &&
        moving.id !== group.id &&
        manageable
      );
    };
    const verdict = (self: Record<string | symbol, unknown>) =>
      self.into === true ? 'into' : extractClosestEdge(self);
    return combine(
      ...(manageable
        ? [
            draggable({
              element,
              getInitialData: () => data,
              onDragStart: () => {
                setDragging(true);
              },
              onDrop: () => {
                setDragging(false);
              },
            }),
          ]
        : []),
      dropTargetForElements({
        element,
        canDrop: ({ source }) => accepts(source.data),
        getData: ({ input, source }) => {
          const edged = attachClosestEdge(data, {
            element,
            input,
            allowedEdges: ['left', 'right'],
          });
          const rect = element.getBoundingClientRect();
          const share = (input.clientX - rect.left) / Math.max(rect.width, 1);
          // resources always go inside; a group goes beside it near the card's edges
          const into = resourceOf(source.data) !== null || (share > 0.25 && share < 0.75);
          return { ...edged, into };
        },
        onDrag: ({ self }) => {
          setZone(verdict(self.data));
        },
        onDragLeave: () => {
          setZone(null);
        },
        onDrop: ({ self, source }) => {
          const where = verdict(self.data);
          setZone(null);
          const resource = resourceOf(source.data);
          if (resource) {
            onDropResource?.(resource);
            return;
          }
          const moving = groupOf(source.data);
          if (!moving) return;
          if (where === 'left') onDropGroup?.(moving.id, { before_id: group.id });
          else if (where === 'right') onDropGroup?.(moving.id, { after_id: group.id });
          else onDropGroup?.(moving.id);
        },
      }),
    );
  }, [group.id, group.workspace_id, manageable, onDropGroup, onDropResource]);

  const hasMenu =
    actions.onCopyLink ||
    actions.onRename ||
    actions.onMove ||
    actions.onMoveOut ||
    actions.onDissolve;
  const hiddenText = t('groups.hiddenCount', { count: hidden });

  return (
    <li
      ref={ref}
      className={styles.card}
      data-testid="group-card"
      data-group-id={group.id}
      data-dragging={dragging || undefined}
      data-static={!manageable || undefined}
      data-drop={zone ?? undefined}
    >
      <button
        type="button"
        className={styles.open}
        data-testid="group-open"
        // the whole number, for a screen reader, whatever the counter abbreviates
        aria-label={t('groups.open', { name: group.name, count: items.length })}
        draggable={false}
        onClick={actions.onOpen}
      >
        <span className={styles.preview} data-empty={items.length === 0 || undefined}>
          {items.length === 0 ? (
            <span className={styles.emptyMark} data-testid="group-empty">
              <Icons.group />
              {t('groups.emptyShort')}
            </span>
          ) : (
            shown.map((child) => <Mini key={`${child.kind}:${child.id}`} child={child} />)
          )}
          {hidden > 0 && (
            <span className={styles.counter} data-testid="group-counter" title={hiddenText}>
              {hiddenLabel(hidden)}
            </span>
          )}
        </span>
        <span className={styles.name}>
          <span className={styles.nameText} title={group.name}>
            {group.name}
          </span>
        </span>
        {location && (
          <span className={styles.location} data-testid="resource-location">
            <Icons.folder /> {location}
          </span>
        )}
      </button>
      <ResourceLabelChips
        labels={group.labels}
        label={t('resourceLabels.field')}
        className={styles.labels ?? ''}
      />
      {hasMenu && (
        <div className={styles.actions}>
          <Popover
            label={t('groups.menu', { name: group.name })}
            width={240}
            alignEnd
            trigger={(props) => (
              <button
                type="button"
                {...props}
                className={styles.menu}
                aria-label={t('groups.menu', { name: group.name })}
                data-testid="group-menu"
              >
                <Icons.more />
              </button>
            )}
          >
            {(close) => (
              <MenuList>
                {actions.onCopyLink && (
                  <MenuRow>
                    <MenuItem
                      icon={<Icons.link />}
                      data-testid="group-copy-link"
                      onClick={() => {
                        close();
                        actions.onCopyLink?.();
                      }}
                    >
                      {t('links.copy')}
                    </MenuItem>
                  </MenuRow>
                )}
                {actions.onLabels && (
                  <MenuRow>
                    <MenuItem
                      icon={<Icons.tag />}
                      data-testid="group-labels"
                      onClick={() => {
                        close();
                        actions.onLabels?.();
                      }}
                    >
                      {t('resourceLabels.action')}
                    </MenuItem>
                  </MenuRow>
                )}
                {actions.onRename && (
                  <MenuRow>
                    <MenuItem
                      icon={<Icons.edit />}
                      onClick={() => {
                        close();
                        actions.onRename?.();
                      }}
                    >
                      {t('groups.rename')}
                    </MenuItem>
                  </MenuRow>
                )}
                {actions.onMove && (
                  <MenuRow>
                    <MenuItem
                      icon={<Icons.folder />}
                      onClick={() => {
                        close();
                        actions.onMove?.();
                      }}
                    >
                      {t('groups.moveTo')}
                    </MenuItem>
                  </MenuRow>
                )}
                {actions.onMoveOut && (
                  <MenuRow>
                    <MenuItem
                      icon={<Icons.chevronLeft />}
                      onClick={() => {
                        close();
                        actions.onMoveOut?.();
                      }}
                    >
                      {t('groups.moveOut')}
                    </MenuItem>
                  </MenuRow>
                )}
                {actions.onDissolve && (
                  <MenuRow separated>
                    <MenuItem
                      icon={<Icons.group />}
                      onClick={() => {
                        close();
                        actions.onDissolve?.();
                      }}
                    >
                      {t('groups.dissolve')}
                    </MenuItem>
                  </MenuRow>
                )}
              </MenuList>
            )}
          </Popover>
        </div>
      )}
    </li>
  );
}
